import React, { useState, useEffect } from 'react';

/**
 * ColorPickerInput - Color picker input field
 * 
 * @param {Object} props
 * @param {Object} props.feature - Feature object from RuleEngine
 * @param {string} props.value - Current color value (hex, rgb, etc.)
 * @param {Function} props.onChange - Change handler
 * @param {string} props.format - Color format ('hex', 'rgb', 'hsl')
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {string} props.className - Additional CSS classes
 */
const ColorPickerInput = ({ 
    feature, 
    value = '#000000', 
    onChange,
    format = 'hex',
    disabled = false, 
    className = ''
}) => {
    const [color, setColor] = useState(value);
    const [showPicker, setShowPicker] = useState(false);
    const displayText = feature?.property?.displayText || feature?.name || 'Color';
    
    // Update local color state when value prop changes
    useEffect(() => {
        setColor(value || '#000000');
    }, [value]);
    
    const handleColorChange = (e) => {
        let newColor = e.target.value;
        
        // Validate hex format
        if (format === 'hex') {
            // Remove # if present and validate
            const hex = newColor.startsWith('#') ? newColor.substring(1) : newColor;
            if (/^[0-9a-fA-F]{0,6}$/.test(hex)) {
                newColor = `#${hex}`;
                setColor(newColor);
                onChange({ target: { value: newColor } });
            }
        } else {
            // For now, just accept any value for rgb/hsl
            setColor(newColor);
            onChange({ target: { value: newColor } });
        }
    };
    
    const handlePickerToggle = () => {
        if (!disabled) {
            setShowPicker(!showPicker);
        }
    };
    
    const handlePickerChange = (e) => {
        const newColor = e.target.value;
        setColor(newColor);
        onChange({ target: { value: newColor } });
    };
    
    // Ensure color is a valid hex for display
    const displayColor = /^#[0-9A-Fa-f]{6}$/.test(color) ? color : '#000000';
    
    return (
        <div 
            className="colorpicker-input-wrapper"
            style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '4px',
                position: 'relative'
            }}
        >
            <label style={{
                fontSize: '0.85rem',
                fontWeight: 600,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <span>{displayText}</span>
                {feature?.property?.isOptional !== false && (
                    <span style={{ color: 'var(--error)', fontSize: '0.75rem' }}>*</span>
                )}
            </label>
            
            {/* Color preview and input container */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
            }}>
                {/* Color preview box */}
                <div 
                    onClick={handlePickerToggle}
                    style={{
                        width: '40px',
                        height: '40px',
                        backgroundColor: displayColor,
                        border: `2px solid ${disabled ? 'var(--border)' : 'var(--accent)'}`,
                        borderRadius: '4px',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        opacity: disabled ? 0.6 : 1,
                        boxShadow: showPicker ? '0 0 0 2px var(--accent)' : 'none',
                        transition: 'box-shadow 0.15s'
                    }}
                />
                
                {/* Color input */}
                <input
                    className={className}
                    type="text"
                    value={color}
                    onChange={handleColorChange}
                    placeholder="#RRGGBB"
                    disabled={disabled}
                    style={{
                        flex: 1,
                        border: `1px solid ${disabled ? 'var(--border)' : 'var(--border)'}`,
                        fontFamily: 'monospace',
                        fontWeight: 500,
                        outline: 'none',
                        cursor: disabled ? 'not-allowed' : 'text',
                        opacity: disabled ? 0.6 : 1
                    }}
                    disabled={disabled}
                />
                
                {/* Format indicator */}
                <span style={{
                    fontSize: '0.75rem',
                    color: 'var(--text-secondary)',
                    padding: '0 8px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '4px'
                }}>
                    {format.toUpperCase()}
                </span>
            </div>
            
            {/* Native color picker (hidden but functional) */}
            <input
                type="color"
                value={displayColor}
                onChange={handlePickerChange}
                style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    width: '100%',
                    height: '50px',
                    opacity: showPicker ? 1 : 0,
                    visibility: showPicker ? 'visible' : 'hidden',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    background: 'transparent',
                    border: 'none',
                    padding: 0
                }}
                disabled={disabled}
            />
            
            {feature?.property?.description && (
                <span style={{
                    fontSize: '0.75rem',
                    color: 'var(--text-secondary)',
                    fontStyle: 'italic'
                }}>
                    {feature.property.description}
                </span>
            )}
        </div>
    );
};

export default ColorPickerInput;
